/**
 * The /home landing page's live figures.
 *
 * ONE listener on ONE document: aggregates/home_live, written once a minute by
 * the kpi-active-journeys daemon (kpi/ in the aggregator-trainstate repo). The
 * page computes nothing and polls nothing.
 *
 * The document is shared. Active journeys is the only field today; the passenger
 * figures will be added to the same document by the same writer, so that the
 * top-bar tile and the Current passengers box can never disagree about the same
 * instant. Add fields to HomeLiveDoc as they appear — the listener does not
 * change.
 *
 * Requires the `aggregates/{docId}` rule in firestore.rules to be DEPLOYED
 * (authenticated read). Until it is, the writes succeed while reads are denied,
 * which looks like a broken listener rather than a missing rule.
 */
import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';

import { db } from '../firebase';

/** Firestore Timestamp, kept structural so this file needs no SDK type import. */
type FsTimestamp = { toDate: () => Date };

export type ActiveJourneys = {
  /** Journeys whose scheduled span overlaps [now - window_minutes, now]. */
  count?: number | null;
  window_minutes?: number | null;
  /** Scheduled rows with a null line_code or route_id. Non-zero is a data-quality signal. */
  unroutable?: number | null;
  /** True for timetable-derived figures: no coverage gap, so no estimate asterisk. */
  exact?: boolean | null;
};

/**
 * Passengers on board. Three tiers, in descending order of trust — see
 * kpi/README.md. `total_estimate` is their sum and is what the top-bar tile
 * shows; it is computed by the writer so the page never adds up figures itself.
 *
 * Null when the writer's passenger query failed. Null is deliberate rather than
 * the field being absent: writes are merges, so an absent field would leave the
 * previous tick's numbers in place beside a fresh updated_at.
 */
export type PassengersOnboard = {
  afz_units?: number | null;
  afz_passengers?: number | null;
  wifi_units?: number | null;
  wifi_passengers_raw?: number | null;
  wifi_passenger_estimate?: number | null;
  journeys_no_formation?: number | null;
  no_formation_uic_estimate?: number | null;
  no_formation_passenger_estimate?: number | null;
  /** Formation known, neither sensor reported. Contributes 0 to the total. */
  unmeasured_units?: number | null;
  units_total?: number | null;
  journeys_with_formation?: number | null;
  total_estimate?: number | null;
  mean_afz_per_unit?: number | null;
  mean_units_per_journey?: number | null;
  params?: {
    wifi_uplift?: number | null;
    units_per_journey?: number | null;
    window_minutes?: number | null;
  } | null;
};

/**
 * Boardings today. One trip = one passenger boarding one train service, so a
 * passenger changing twice counts three times. AFZ units are counted; the rest
 * are valued at the AFZ mean.
 */
export type TripsDay = {
  operating_date?: string | null;
  afz_units?: number | null;
  afz_boardings?: number | null;
  total_units?: number | null;
  non_afz_units?: number | null;
  extrapolated_boardings?: number | null;
  total_estimate?: number | null;
  mean_boardings_per_afz_unit?: number | null;
  /** Percentage of the day's units that AFZ actually counted. */
  afz_unit_share?: number | null;
  params?: { min_daily_boardings?: number | null } | null;
};

export type RollingWindow = {
  days?: number | null;
  afz_boardings?: number | null;
  extrapolated_boardings?: number | null;
  total_estimate?: number | null;
  /** Below `days` means the window has gaps and the total understates. */
  days_with_data?: number | null;
  days_missing_afz?: number | null;
  first_day?: string | null;
  last_day?: string | null;
};

/**
 * Rolling windows ending today, keyed `days_7` / `days_30`. The keys follow
 * KPI_ROLLING_DAYS on the rollup VM, so the page asks for the windows it expects
 * rather than rendering whatever it finds — a mismatch shows as an em dash.
 */
export type TripsRolling = {
  windows?: Record<string, RollingWindow> | null;
  /** Today is in every window and is not over, so the figures dip after midnight. */
  includes_partial_today?: boolean | null;
  history_first_day?: string | null;
  params?: {
    min_daily_boardings?: number | null;
    rolling_days?: number[] | null;
  } | null;
};

/**
 * VU capacity-message coverage. A message is produced on a DEPARTURE event, so
 * the denominator is departed stops — a stop the train has not reached cannot
 * have one, and counting it as a miss would make the figure climb all day for
 * no real reason.
 *
 * published + failed + untriggered + processing === departed, always: the
 * writer derives `processing` as the remainder so the four can never fail to
 * sum on screen.
 */
export type VuLine = {
  line?: string | null;
  departed?: number | null;
  published?: number | null;
  failed?: number | null;
  /** Departed, but no send cycle ever triggered — quieter than an outright failure. */
  untriggered?: number | null;
  processing?: number | null;
  published_pct?: number | null;
};

export type VuWindow = {
  departed?: number | null;
  published?: number | null;
  failed?: number | null;
  untriggered?: number | null;
  processing?: number | null;
  published_pct?: number | null;
  /** Worst-first: a headline percentage hides a single broken line. */
  lines?: VuLine[] | null;
};

export type VuFailureReason = {
  reason?: string | null;
  raw?: string | null;
  stops?: number | null;
  share?: number | null;
};

export type VuCoverage = {
  windows?: Record<string, VuWindow> | null;
  /** Today's failures grouped by cause, most common first. */
  failure_reasons?: VuFailureReason[] | null;
};

/**
 * Ingest volumes per feed. Unlike everything else on this document these come
 * from Cloud Monitoring rather than Cloud SQL: a row count in a landing table
 * measures messages PERSISTED, and the gap between that and messages RECEIVED
 * is precisely what this box exists to show.
 */
export type FeedDropReason = {
  reason?: string | null;
  count?: number | null;
};

export type FeedRow = {
  name?: string | null;
  /**
   * Null means the feed published no data at all in the window — it is silent,
   * not zero. The writer emits a row with nulls for every expected feed so a
   * dead feed cannot vanish from the list and read as healthy.
   */
  received?: number | null;
  published?: number | null;
  /** Derived as received - published by the writer, so the columns always sum. */
  dropped?: number | null;
  /**
   * Parse and publish failures only. Most drops are the listener working as
   * designed — FzO-BO discards ~58% of what it receives by intent — so these
   * are counted apart from `dropped` rather than being conflated with it.
   */
  errors?: number | null;
  /** Full breakdown, largest first. Detail rather than headline. */
  drop_reasons?: FeedDropReason[] | null;
};

export type FeedWindowData = {
  from?: string | null;
  to?: string | null;
  feeds?: FeedRow[] | null;
};

/** Keyed `hour` and `day`; `day` is since local midnight, not a sliding 24h. */
export type Feeds = {
  as_of?: string | null;
  windows?: Record<string, FeedWindowData> | null;
};

/**
 * Forecast accuracy, written hourly by the accuracy rollup (accuracy/ in the
 * aggregator-trainstate repo) rather than by the kpi daemon. Same document, a
 * different writer and a different clock.
 *
 * TWO FIGURES, ANSWERING DIFFERENT QUESTIONS. `today` is a PARTIAL day that
 * grows as trains depart their target stops, so it is noisy in the morning and
 * must be shown with its n_samples. `last_complete_day` is immutable and is the
 * number to quote. They will not agree, and must not be presented as if they
 * might — on 2026-09-17 today read 0.8956 at 09:27 against the previous full
 * day's 0.926.
 *
 * Null rather than absent when a fetch failed: writes are merges, so an absent
 * field would leave the previous hour's figures in place beside a fresh
 * timestamp.
 */
export type AccuracyCellDoc = {
  /** Samples on the message-carried (displayed) track. The denominator for class_hit_rate. */
  n?: number | null;
  /** Share where the class carried in the VU message matched the actual (0..1). */
  class_hit_rate?: number | null;
  n_high_actual?: number | null;
  /** Share of actual-HOCH samples the message classed NIEDRIG (0..1). */
  high_to_low_miss_rate?: number | null;
  mae_persons?: number | null;
  /** Samples that joined the forecaster's prediction archive. */
  n_model?: number | null;
  /** Hit rate of the class the FORECASTER delivered, over the joined subset. */
  model_class_hit_rate?: number | null;
  model_high_to_low_miss_rate?: number | null;
};

/** Keys: "N+1".."N+5" (only horizons with samples) plus "all". */
export type AccuracyNodeDoc = Record<string, AccuracyCellDoc>;

export type AccuracyDay = {
  day?: string | null;
  /** How much the rate rests on. Always show this beside a partial-day figure. */
  n_samples?: number | null;
  n_journeys?: number | null;
  /** Non-zero means the analysis-api dropped requests upstream. */
  journey_fetch_errors?: number | null;
  /** 'unavailable' means the BigQuery archive could not be read, so every
   *  model_* figure is null while the displayed track is unaffected. A designed
   *  degradation, not an error — but it must be visible. */
  model_source?: string | null;
  model_class_coverage?: number | null;
  pareto_versions?: string[] | null;
  overall?: AccuracyNodeDoc | null;
  by_line?: Record<string, AccuracyNodeDoc> | null;
  by_model_version?: Record<string, AccuracyNodeDoc> | null;
};

export type Accuracy = {
  today?: AccuracyDay | null;
  last_complete_day?: AccuracyDay | null;
  method_version?: string | null;
  /** THIS block's own freshness. Distinct from the document-level updated_at,
   *  which belongs to the kpi daemon and drives the Live dot — the two run on
   *  different clocks (60 seconds against 60 minutes). */
  updated_at?: FsTimestamp | null;
};

export type HomeLiveDoc = {
  active_journeys?: ActiveJourneys | null;
  passengers_onboard?: PassengersOnboard | null;
  trips_day?: TripsDay | null;
  trips_rolling?: TripsRolling | null;
  vu_coverage?: VuCoverage | null;
  feeds?: Feeds | null;
  accuracy?: Accuracy | null;
  service_date?: string | null;
  method_version?: string | null;
  updated_at?: FsTimestamp | null;
};

/**
 * Grey the "Live" dot past this. Roughly 3x the writer's 60s tick — close enough
 * that a genuine stall shows quickly, loose enough that scheduler jitter or one
 * slow query does not make a working page flicker. If KPI_TICK_SECONDS changes on
 * the VM, this moves with it.
 */
export const STALE_AFTER_MS = 3 * 60 * 1000;

/**
 * Staleness is a function of wall-clock time, so no snapshot fires when a
 * document merely goes stale — the same trap `live2/deriveJourney.ts` documents.
 * Hence the timer.
 */
const CLOCK_TICK_MS = 15 * 1000;

/**
 * Accuracy has its OWN staleness threshold, because it has its own writer on a
 * 60-MINUTE clock. Judging accuracy.updated_at against STALE_AFTER_MS above
 * would mark a perfectly healthy block stale within three minutes of every
 * write. ~2.5 hours: long enough to survive one missed timer fire plus the
 * RandomizedDelaySec jitter, short enough that two missed fires show.
 */
export const ACCURACY_STALE_AFTER_MS = 150 * 60 * 1000;

export type HomeLive = {
  doc: HomeLiveDoc | null;
  /** True once the listener has produced a result, present or absent. */
  loaded: boolean;
  error: Error | null;
  updatedMs: number | null;
  /** No document, no timestamp, or the timestamp is older than STALE_AFTER_MS. */
  stale: boolean;
};

export function useHomeLive(): HomeLive {
  const [snap, setSnap] = useState<HomeLiveDoc | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [nowMs, setNowMs] = useState<number>(() => Date.now());

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'aggregates', 'home_live'),
      (s) => {
        setSnap(s.exists() ? (s.data() as HomeLiveDoc) : null);
        setLoaded(true);
        setError(null);
      },
      (err) => {
        // permission-denied here almost always means the aggregates rule has not
        // been deployed, not that the writer has stopped.
        console.error('❌ aggregates/home_live onSnapshot error', err);
        setError(err as Error);
        setLoaded(true);
      },
    );
    return unsub;
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), CLOCK_TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  const updatedMs = snap?.updated_at?.toDate?.().getTime() ?? null;
  const stale = updatedMs === null || nowMs - updatedMs > STALE_AFTER_MS;

  return { doc: snap, loaded, error, updatedMs, stale };
}
